import React, { RefObject, useRef } from 'react';
import { Chip } from '@naf-simba/elements-chips';
import { Text, TextVariant } from '@naf/text';
import { Close } from '@styled-icons/material/Close';
import { useScrollBlock } from '@hooks/useScrollBlock';
import useOutsideAlerter from '@hooks/useOutsideAlerter';
import useSelector from '@redux/typedHooks';
import { SearchBar } from '../SearchBar/Searchbar';
import SearchOverlay from '../searchOverlay/SearchOverlay';
import { fallbackPopularSearchTerms, fallbackSearchPlaceholder, fallbackSearchTitle } from './SearchModal.utils';
import * as S from './SearchModal.styles';

interface SearchModalProps {
  isSearchActive: boolean;
  navBarRef: RefObject<HTMLDivElement>;
  onClose: () => void;
  onCloseResults: () => void;
  onPopularSearchClick: (searchText: string) => void;
  onSearch: (event: React.FormEvent<HTMLFormElement>) => boolean;
  onSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const SearchModal = ({
  isSearchActive,
  navBarRef,
  onClose,
  onCloseResults,
  onPopularSearchClick,
  onSearch,
  onSearchInputChange,
}: SearchModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const searchSettings = useSelector((state) => state.globalSettings.settings?.search);

  const searchTitle = searchSettings?.title || fallbackSearchTitle;
  const searchPlaceholder = searchSettings?.placeholder || fallbackSearchPlaceholder;
  const popularSearchTerms =
    searchSettings?.popularSearchTerms && searchSettings.popularSearchTerms.length > 0
      ? searchSettings.popularSearchTerms
      : fallbackPopularSearchTerms;

  useScrollBlock();
  useOutsideAlerter(modalRef, true, onClose);

  return (
    <S.Modal
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-search-modal-title"
      $isSearchActive={isSearchActive}
    >
      <S.Header>
        <Text id="site-search-modal-title" tag="h2" variant={TextVariant.Header2}>
          {searchTitle}
        </Text>
        <S.CloseButton type="button" aria-label="Lukk søk" onClick={onClose}>
          <Close size={24} aria-hidden="true" focusable="false" />
        </S.CloseButton>
      </S.Header>
      <S.Content>
        <S.SearchFieldWrapper>
          <SearchBar
            autoFocus
            onSearch={onSearch}
            onSearchInputChange={onSearchInputChange}
            placeholder={searchPlaceholder}
          />
        </S.SearchFieldWrapper>
        <S.Divider />
        {isSearchActive ? (
          <S.ResultsWrapper>
            <SearchOverlay navBarRef={navBarRef} isInModal onCloseResults={onCloseResults} />
          </S.ResultsWrapper>
        ) : (
          <S.PopularSearches>
            <Text tag="h3" variant={TextVariant.Header3}>
              Populære søk
            </Text>
            <S.ChipsWrapper>
              {popularSearchTerms.map((searchText) => (
                <Chip
                  key={searchText}
                  variant="outlined"
                  color="primary"
                  label={searchText}
                  onClick={() => onPopularSearchClick(searchText)}
                />
              ))}
            </S.ChipsWrapper>
          </S.PopularSearches>
        )}
      </S.Content>
    </S.Modal>
  );
};
